import React, { useState, useEffect, useCallback } from 'react';
import './styles/global.css';

import ProgressBar from './components/ProgressBar';
import SuccessScreen from './components/SuccessScreen';
import ErrorScreen from './components/ErrorScreen';
import Dashboard from './Dashboard';

import S0_Identity from './sections/S0_Identity';
import S1_OverallShift from './sections/S1_OverallShift';
import S2_CustomerFeedback from './sections/S2_CustomerFeedback';
import S3_Equipment from './sections/S3_Equipment';
import S4_Ingredients from './sections/S4_Ingredients';
import S5_Hygiene from './sections/S5_Hygiene';
import S6_Staff from './sections/S6_Staff';
import S7_DeliveryPlatforms from './sections/S7_DeliveryPlatforms';
import S8_Other from './sections/S8_Other';
import supabase from './supabaseClient';
import { uploadToSpaces } from './lib/spacesUpload';

// PascalCase aliases to satisfy react/jsx-pascal-case lint rule
const SecIdentity = S0_Identity;
const SecOverallShift = S1_OverallShift;
const SecCustomerFeedback = S2_CustomerFeedback;
const SecEquipment = S3_Equipment;
const SecIngredients = S4_Ingredients;
const SecHygiene = S5_Hygiene;
const SecStaff = S6_Staff;
const SecDelivery = S7_DeliveryPlatforms;
const SecOther = S8_Other;

// ── Constants ──────────────────────────────────────────────────────────────

const DRAFT_KEY = 'tmaami_daily_draft';
const TOTAL_SECTIONS = 9; // S0 through S8

const OUTLET_MAP = {
  velachery: 'Velachery',
  tnagar: 'T. Nagar',
  annanagar: 'Anna Nagar',
};

const PIN_MAP = {
  Velachery: process.env.REACT_APP_VELACHERY_PIN,
  'T. Nagar': process.env.REACT_APP_TNAGAR_PIN,
  'Anna Nagar': process.env.REACT_APP_ANNANAGAR_PIN,
};

const MAX_ATTEMPTS = 3;

function getToday() {
  return new Date().toISOString().split('T')[0];
}

function getEmptyForm(outletFromUrl) {
  return {
    report_date: getToday(),
    outlet: outletFromUrl || '',
    submitted_by: '',
    // S1
    shift_overall: '',
    bills_today: '',
    peak_hour: '',
    customer_count: '',
    // S2
    customer_feedback_pos: [],
    customer_feedback_neg: [],
    negative_feedback_detail: '',
    feedback_notes: '',
    // S3
    equipment_issues: [],
    equipment_detail: '',
    equipment_notes: '',
    // S4
    ingredient_issues: [],
    stock_issues_text: '',
    ingredient_notes: '',
    // S5
    hygiene_rating: '',
    hygiene_issues: [],
    hygiene_notes: '',
    // S6
    staff_issues: [],
    staff_notes: '',
    // S7
    delivery_platform: '',
    platform_notes: '',
    // S8
    voids_today: false,
    voids_detail: '',
    other_notes: '',
    one_thing_better: '',
  };
}

// ── PIN Screen ─────────────────────────────────────────────────────────────

function PinScreen({ outlet, onSuccess }) {
  const [pin, setPin] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState('');
  const locked = attempts >= MAX_ATTEMPTS;

  function handleSubmit(e) {
    e.preventDefault();
    if (locked) return;
    const correct = PIN_MAP[outlet];
    // If no PIN is configured (e.g. preview/dev build with placeholder .env), auto-pass
    if (!correct || correct === 'your_4_digit_pin') { onSuccess(); return; }
    if (pin === correct) {
      onSuccess();
    } else {
      const next = attempts + 1;
      setAttempts(next);
      setPin('');
      if (next >= MAX_ATTEMPTS) {
        setError('');
      } else {
        setError(
          `Incorrect PIN. ${MAX_ATTEMPTS - next} attempt${
            MAX_ATTEMPTS - next === 1 ? '' : 's'
          } remaining.`
        );
      }
    }
  }

  return (
    <div className="pin-screen">
      <div className="pin-logo">Taiwan Maami</div>
      <p className="pin-subtitle">Daily outlet report</p>
      <div className="pin-card">
        <h2>Enter your outlet PIN</h2>
        <p className="pin-outlet">{outlet}</p>
        {locked ? (
          <p className="pin-locked">
            Too many attempts — please contact your manager.
          </p>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <input
              className={`pin-input${error ? ' has-error' : ''}`}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={pin}
              onChange={(e) => {
                setError('');
                setPin(e.target.value.replace(/\D/g, '').slice(0, 4));
              }}
              placeholder="••••"
              autoFocus
              autoComplete="off"
            />
            {error && <p className="pin-error">{error}</p>}
            <button className="btn-pin" type="submit" disabled={pin.length < 1}>
              Continue
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Draft Banner ───────────────────────────────────────────────────────────

function DraftBanner({ onContinue, onFresh }) {
  return (
    <div className="draft-banner">
      <p>You have an unfinished report from earlier — continue?</p>
      <div className="draft-banner-actions">
        <button className="btn-draft-continue" type="button" onClick={onContinue}>
          Continue
        </button>
        <button className="btn-draft-fresh" type="button" onClick={onFresh}>
          Start fresh
        </button>
      </div>
    </div>
  );
}

// ── Main App ───────────────────────────────────────────────────────────────

function App() {
  const params = new URLSearchParams(window.location.search);
  const viewParam = params.get('view') || '';
  const hashPath = window.location.hash.replace('#', '').split('?')[0];
  const isDashboard = viewParam === 'dashboard' || hashPath === '/dashboard';

  if (isDashboard) return <Dashboard />;

  const outletParam = (params.get('outlet') || '').toLowerCase();
  const outletFromUrl = OUTLET_MAP[outletParam] || null;
  const outletLocked = !!outletFromUrl;

  return <OutletForm outletFromUrl={outletFromUrl} outletLocked={outletLocked} />;
}

// ── Outlet Form ────────────────────────────────────────────────────────────

function OutletForm({ outletFromUrl, outletLocked }) {
  const [pinPassed, setPinPassed] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [errors, setErrors] = useState({});
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);

  const [formData, setFormData] = useState(() => getEmptyForm(outletFromUrl));

  // ── Draft auto-save ──────────────────────────────────────────────────────

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        if (!outletLocked || draft.outlet === outletFromUrl) {
          setShowDraftBanner(true);
          window.__tmaami_draft = draft;
        }
      }
    } catch (_) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDraft = useCallback(
    (data) => {
      if (!pinPassed) return;
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify(data)); } catch (_) {}
    },
    [pinPassed]
  );

  function handleFieldChange(field, value) {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      saveDraft(next);
      return next;
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  function handleContinueDraft() {
    const draft = window.__tmaami_draft;
    if (draft) {
      setFormData((prev) => ({
        ...prev,
        ...draft,
        outlet: outletLocked ? outletFromUrl : (draft.outlet || prev.outlet),
        staff_issues: Array.isArray(draft.staff_issues)
          ? draft.staff_issues
          : draft.staff_issues ? [draft.staff_issues] : [],
        voids_today: !!draft.voids_today,
      }));
    }
    setShowDraftBanner(false);
  }

  function handleFreshStart() {
    localStorage.removeItem(DRAFT_KEY);
    window.__tmaami_draft = null;
    setShowDraftBanner(false);
    setFormData(getEmptyForm(outletFromUrl));
    setPhotoFile(null);
  }

  // ── Validation ───────────────────────────────────────────────────────────

  function validateSection(section) {
    const errs = {};
    const d = formData;

    if (section === 0) {
      if (!d.report_date) errs.report_date = 'Please select a date.';
      if (!d.outlet) errs.outlet = 'Please select an outlet.';
      if (!d.submitted_by.trim()) errs.submitted_by = 'Please enter your name.';
    }

    if (section === 1) {
      if (!d.shift_overall) errs.shift_overall = 'Please select how the shift went.';
      if (d.bills_today === '' || d.bills_today === null || d.bills_today === undefined) {
        errs.bills_today = 'Please enter the number of bills today.';
      } else if (Number(d.bills_today) < 0) {
        errs.bills_today = 'Bills cannot be negative.';
      }
      if (!d.peak_hour) errs.peak_hour = 'Please select the peak hour.';
    }

    if (section === 2) {
      const negSelected = d.customer_feedback_neg || [];
      const hasRealNeg = negSelected.length > 0 && !negSelected.every((t) => t === 'Nothing to flag');
      if (hasRealNeg) {
        if (!d.negative_feedback_detail || d.negative_feedback_detail.trim().length < 15) {
          errs.negative_feedback_detail = 'Please describe the feedback (at least 15 characters).';
        }
      }
    }

    if (section === 3) {
      const selected = d.equipment_issues || [];
      const hasRealIssue = selected.length > 0 && !selected.every((t) => t === 'No issues');
      if (hasRealIssue) {
        if (!d.equipment_detail || d.equipment_detail.trim().length < 15) {
          errs.equipment_detail = 'Please describe the equipment issue (at least 15 characters).';
        }
      }
    }

    if (section === 4) {
      if (!d.stock_issues_text || d.stock_issues_text.trim().length < 3) {
        errs.stock_issues_text = "Please enter stock status (e.g. 'None' if nothing ran out) — at least 3 characters.";
      }
    }

    if (section === 8) {
      if (d.voids_today && (!d.voids_detail || d.voids_detail.trim().length < 10)) {
        errs.voids_detail = 'Please describe the voids/refunds (at least 10 characters).';
      }
      if (!d.one_thing_better || d.one_thing_better.trim().length < 20) {
        errs.one_thing_better = 'Please enter at least 20 characters describing what would have made today better.';
      }
    }

    return errs;
  }

  function handleNext() {
    const errs = validateSection(currentSection);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setCurrentSection((s) => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleBack() {
    setErrors({});
    setCurrentSection((s) => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ── Submission ───────────────────────────────────────────────────────────

  async function handleSubmit() {
    // Final validation on last section
    const errs = validateSection(currentSection);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSubmitting(true);
    setSubmitError(false);
    setUploadProgress(0);
    setUploadStatus('');

    // 1. Upload photo if present
    let photoUrl = null;
    if (photoFile) {
      setUploadStatus('uploading');
      try {
        photoUrl = await uploadToSpaces(photoFile, 'daily-reports', (pct) => setUploadProgress(pct));
        setUploadStatus('done');
      } catch (uploadErr) {
        console.warn('Photo upload failed, submitting without photo:', uploadErr);
        setUploadStatus('failed');
        photoUrl = null;
      }
    }

    // 2. Build payload
    const d = formData;
    const negSelected = d.customer_feedback_neg || [];
    const hasRealNeg = negSelected.length > 0 && !negSelected.every((t) => t === 'Nothing to flag');
    const eqSelected = d.equipment_issues || [];
    const hasRealEquip = eqSelected.length > 0 && !eqSelected.every((t) => t === 'No issues');

    const payload = {
      report_date: d.report_date,
      outlet: d.outlet,
      submitted_by: d.submitted_by.trim(),
      shift_overall: d.shift_overall,
      bills_today: d.bills_today !== '' ? Number(d.bills_today) : null,
      peak_hour: d.peak_hour || null,
      customer_count: d.customer_count || null,
      customer_feedback_pos: d.customer_feedback_pos.length > 0 ? d.customer_feedback_pos : null,
      customer_feedback_neg: negSelected.length > 0 ? negSelected : null,
      negative_feedback_detail: hasRealNeg ? (d.negative_feedback_detail.trim() || null) : null,
      feedback_notes: d.feedback_notes.trim() || null,
      equipment_issues: eqSelected.length > 0 ? eqSelected : null,
      equipment_detail: hasRealEquip ? (d.equipment_detail.trim() || null) : null,
      equipment_notes: d.equipment_notes.trim() || null,
      ingredient_issues: d.ingredient_issues.length > 0 ? d.ingredient_issues : null,
      stock_issues_text: d.stock_issues_text.trim() || null,
      ingredient_notes: d.ingredient_notes.trim() || null,
      hygiene_rating: d.hygiene_rating || null,
      hygiene_issues: d.hygiene_issues.length > 0 ? d.hygiene_issues : null,
      hygiene_notes: d.hygiene_notes.trim() || null,
      staff_issues: d.staff_issues.length > 0 ? d.staff_issues : null,
      staff_notes: d.staff_notes.trim() || null,
      delivery_platform: d.delivery_platform || null,
      platform_notes: d.platform_notes.trim() || null,
      voids_today: !!d.voids_today,
      voids_detail: d.voids_today ? (d.voids_detail.trim() || null) : null,
      other_notes: d.other_notes.trim() || null,
      one_thing_better: d.one_thing_better.trim() || null,
      photo_url: photoUrl,
    };

    // 3. Insert to Supabase
    try {
      const { error } = await supabase.from('outlet_daily_reports').insert([payload]);
      if (error) throw error;
      localStorage.removeItem(DRAFT_KEY);
      setSubmitted(true);
    } catch (err) {
      console.error('Submission error:', err);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  }

  function handleRetry() {
    setSubmitError(false);
    setUploadStatus('');
    setUploadProgress(0);
  }

  // ── Render ───────────────────────────────────────────────────────────────

  if (submitted) return <SuccessScreen />;
  if (submitError) return <ErrorScreen onRetry={handleRetry} />;

  const pinOutlet = outletFromUrl || formData.outlet;

  if (!pinPassed && outletLocked) {
    return <PinScreen outlet={pinOutlet} onSuccess={() => setPinPassed(true)} />;
  }

  const sectionComponents = [
    <SecIdentity data={formData} onChange={handleFieldChange} errors={errors} outletLocked={outletLocked} />,
    <SecOverallShift data={formData} onChange={handleFieldChange} errors={errors} />,
    <SecCustomerFeedback data={formData} onChange={handleFieldChange} errors={errors} />,
    <SecEquipment data={formData} onChange={handleFieldChange} errors={errors} />,
    <SecIngredients data={formData} onChange={handleFieldChange} errors={errors} />,
    <SecHygiene data={formData} onChange={handleFieldChange} />,
    <SecStaff data={formData} onChange={handleFieldChange} />,
    <SecDelivery data={formData} onChange={handleFieldChange} />,
    <SecOther data={formData} onChange={handleFieldChange} errors={errors} photoFile={photoFile} onPhotoChange={setPhotoFile} />,
  ];

  const isLastSection = currentSection === TOTAL_SECTIONS - 1;
  const outletLabel = formData.outlet || (outletLocked ? outletFromUrl : 'Select outlet');

  const needsPinAfterOutletSelect =
    !outletLocked && !pinPassed && formData.outlet && currentSection > 0;

  if (needsPinAfterOutletSelect) {
    return <PinScreen outlet={formData.outlet} onSuccess={() => setPinPassed(true)} />;
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <h1>Daily Report</h1>
        {outletLabel && <div className="outlet-label">{outletLabel}</div>}
        {pinPassed && <ProgressBar current={currentSection} total={TOTAL_SECTIONS} />}
      </header>

      {showDraftBanner && pinPassed && (
        <DraftBanner onContinue={handleContinueDraft} onFresh={handleFreshStart} />
      )}

      <main>
        {pinPassed ? sectionComponents[currentSection] : sectionComponents[0]}
      </main>

      {isLastSection && submitting && uploadStatus === 'uploading' && (
        <div className="upload-progress-wrap">
          <p className="upload-progress-label">Uploading photo… {uploadProgress}%</p>
          <div className="upload-progress-bar">
            <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
          </div>
        </div>
      )}

      <div className="nav-buttons">
        {currentSection > 0 && pinPassed && (
          <button className="btn btn-secondary" type="button" onClick={handleBack} disabled={submitting}>
            Back
          </button>
        )}

        {!pinPassed && !outletLocked ? (
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => {
              const errs = validateSection(0);
              if (Object.keys(errs).length > 0) { setErrors(errs); return; }
              setErrors({});
              setCurrentSection(1);
            }}
          >
            Next
          </button>
        ) : isLastSection ? (
          <button className="btn btn-submit" type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <span className="spinner" aria-hidden="true" />
                {uploadStatus === 'uploading' ? 'Uploading…' : 'Sending…'}
              </>
            ) : (
              "Send today's report"
            )}
          </button>
        ) : (
          <button className="btn btn-primary" type="button" onClick={handleNext} disabled={submitting}>
            Next
          </button>
        )}
      </div>
    </div>
  );
}

export default App;
